// frontend/src/api/auth.js
import client, { setAccessToken, setCsrfToken } from "./client";

/**
 * Auth API — thin wrappers around the /auth endpoints.
 */

/**
 * Log in with email and password.
 *
 * The refresh token is NOT stored client-side — the server sets it as an
 * HttpOnly cookie that the browser sends automatically (and JS can't read).
 * We only keep the access token (caller stores it) and the double-submit CSRF
 * token (held in client memory) so subsequent state-changing requests pass.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{access_token: string, token_type: string, csrf_token: string}>}
 */
export async function login(email, password) {
  // Backend expects a JSON body with { email, password }
  const response = await client.post("/api/auth/login", { email, password });
  if (response.data?.csrf_token) {
    setCsrfToken(response.data.csrf_token);
  }
  return response.data;
}

/**
 * Single-flight guard for refreshToken().
 *
 * Refresh tokens are SINGLE-USE: each call rotates the cookie. If two requests
 * fire before the first response updates the cookie, the second one re-uses
 * the now-consumed token and the backend's reuse-detection nukes the whole
 * family.  Concurrent callers (React 18 StrictMode's double-effect, two
 * components mounting at once, or the 401 retry interceptor racing with the
 * AuthContext bootstrap) all share ONE in-flight promise via this guard.
 */
let _refreshInFlight = null;

/**
 * Exchange the HttpOnly refresh cookie for a fresh access token.
 *
 * Sends no body — the browser attaches the refresh_token cookie automatically
 * (the client uses withCredentials). On success the rotated access token is
 * persisted and the rotated CSRF token is kept in memory.
 * @returns {Promise<string>} the new access token
 */
export async function refreshToken() {
  if (_refreshInFlight) {
    return _refreshInFlight;
  }
  _refreshInFlight = (async () => {
    try {
      const response = await client.post("/api/auth/refresh");
      const { access_token: accessToken, csrf_token: csrf } = response.data;
      setAccessToken(accessToken);
      if (csrf) {
        setCsrfToken(csrf);
      }
      return accessToken;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

/**
 * Switch the active organisation: re-issue tokens scoped to `orgId`.
 *
 * The new access token carries the new `org_id` claim (so every subsequent
 * request is scoped to that org) and a rotated CSRF token; both are stored in
 * client memory. The caller (AuthContext) updates React state with the returned
 * token and reloads the org context.
 * @param {string} orgId target organisation UUID
 * @returns {Promise<string>} the new access token
 */
export async function switchOrg(orgId) {
  const response = await client.post("/api/auth/switch-org", { org_id: orgId });
  const { access_token: accessToken, csrf_token: csrf } = response.data;
  setAccessToken(accessToken);
  if (csrf) {
    setCsrfToken(csrf);
  }
  return accessToken;
}

/**
 * Log out: revoke the session server-side, clear local credentials, redirect.
 *
 * POSTs /api/auth/logout (blacklists the access token, invalidates the refresh
 * family, and clears the cookies). The network call is best-effort — local
 * state is cleared and the user is sent to /login regardless of the result.
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await client.post("/api/auth/logout");
  } catch {
    // Best-effort: clear local state and redirect even if the call fails.
  }
  setAccessToken(null);
  setCsrfToken(null);
  window.location.href = "/login";
}

/**
 * Register a new admin account.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} Created admin object
 */
export async function register(email, password) {
  const response = await client.post("/api/auth/register", { email, password });
  return response.data;
}

/**
 * Fetch the currently authenticated admin's profile.
 * @returns {Promise<object>} Admin object
 */
export async function getMe() {
  const response = await client.get("/api/auth/me");
  return response.data;
}

/**
 * Update the current admin's editable profile fields.
 * @param {{ display_name: string|null }} fields
 * @returns {Promise<object>} The updated admin object
 */
export async function updateMe(fields) {
  const response = await client.put("/api/auth/me", fields);
  return response.data;
}

export async function changePassword(currentPassword, newPassword) {
  const response = await client.post("/api/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
}

/**
 * Complete an MFA-gated login: exchange the short-lived mfa_token + a TOTP or
 * backup code for a full session. On success the rotated CSRF token is held in
 * memory (same as login); the caller stores the access token.
 * @param {string} mfaToken short-lived token from the login response
 * @param {string} code TOTP code or one-time backup code
 * @returns {Promise<{access_token: string, csrf_token: string}>}
 */
export async function mfaChallenge(mfaToken, code) {
  const response = await client.post("/api/auth/mfa/challenge", {
    mfa_token: mfaToken,
    code,
  });
  if (response.data?.csrf_token) {
    setCsrfToken(response.data.csrf_token);
  }
  return response.data;
}

/**
 * Get the current user's MFA status.
 * @returns {Promise<{mfa_enabled: boolean, backup_codes_remaining: number}>}
 */
export async function mfaStatus() {
  const response = await client.get("/api/auth/mfa/status");
  return response.data;
}

/**
 * Begin MFA enrolment — returns a provisioning URI and QR image (base64 PNG).
 * @returns {Promise<{totp_uri: string, qr_code_image_base64: string}>}
 */
export async function mfaSetup() {
  const response = await client.post("/api/auth/mfa/setup");
  return response.data;
}

/**
 * Confirm the first TOTP code to enable MFA; returns one-time backup codes.
 * @param {string} code 6-digit TOTP code
 * @returns {Promise<{backup_codes: string[]}>}
 */
export async function mfaConfirm(code) {
  const response = await client.post("/api/auth/mfa/confirm", { code });
  return response.data;
}

/**
 * Disable MFA after verifying a current TOTP code.
 * @param {string} code 6-digit TOTP code
 * @returns {Promise<{detail: string}>}
 */
export async function mfaDisable(code) {
  const response = await client.post("/api/auth/mfa/disable", { code });
  return response.data;
}

/**
 * Record the current user's acceptance of a legal document into the consent
 * ledger (best-effort; 401 for anonymous visitors is expected and ignored by
 * callers). document_type: tos | privacy_policy | dpa | cookie_policy.
 * @param {string} documentType
 * @returns {Promise<object>}
 */
export async function recordConsent(documentType) {
  const response = await client.post("/api/consent", {
    document_type: documentType,
  });
  return response.data;
}

/**
 * Record a cookie-policy acceptance for an anonymous (logged-out) visitor into
 * the consent ledger — a user-less row keyed to the anonymised request IP, so
 * cookie consent is provable for all visitors, not just signed-in users.
 * @returns {Promise<object>}
 */
export async function recordCookieConsent() {
  const response = await client.post("/api/consent/cookie");
  return response.data;
}

/**
 * Whether the current user has accepted the CURRENT version of each required
 * legal document — drives the re-acceptance prompt after a version bump.
 * @returns {Promise<Array<{document_type:string, current_version:string, accepted:boolean}>>}
 */
export async function getRequiredConsents() {
  const response = await client.get("/api/consent/required");
  return response.data;
}
